import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,  // Send cookies with every request
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't reload for auth endpoints - they handle 401 themselves
    const isAuthEndpoint = error.config?.url?.includes('/auth/');

    if (error.response && error.response.status === 401 && !isAuthEndpoint) {
      // Redirect to login by reloading page (AuthContext will handle it)
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

/**
 * Register a new visitor
 * @param {Object} visitorData - Visitor information
 * @returns {Promise} Visitor response data
 */
export const registerVisitor = async (visitorData) => {
  try {
    const response = await api.post('/visitors/register', visitorData);
    return response.data;
  } catch (error) {
    console.error('Error registering visitor:', error);
    throw error;
  }
};

/**
 * Get all currently checked-in visitors
 * @returns {Promise} Array of current visitors
 */
export const getCurrentVisitors = async () => {
  try {
    const response = await api.get('/visitors/current');
    return response.data;
  } catch (error) {
    console.error('Error fetching current visitors:', error);
    throw error;
  }
};

/**
 * Check out a visitor
 * @param {number} visitorId - ID of the visitor to check out
 * @returns {Promise} Checkout response
 */
export const checkoutVisitor = async (visitorId) => {
  try {
    const response = await api.patch(`/visitors/${visitorId}/checkout`);
    return response.data;
  } catch (error) {
    console.error('Error checking out visitor:', error);
    throw error;
  }
};

/**
 * Get visitor history
 * @param {number} days - Number of days to retrieve (default: 30)
 * @returns {Promise} Array of historical visitors
 */
export const getVisitorHistory = async (days = 30) => {
  try {
    const response = await api.get(`/visitors/history?days=${days}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching visitor history:', error);
    throw error;
  }
};

/**
 * Delete a visitor
 * @param {number} visitorId - ID of the visitor to delete
 * @returns {Promise} Delete response
 */
export const deleteVisitor = async (visitorId) => {
  try {
    const response = await api.delete(`/visitors/${visitorId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting visitor:', error);
    throw error;
  }
};

/**
 * Get privacy notice configuration (contact email)
 * @returns {Promise} Privacy config object
 */
export const getPrivacyConfig = async () => {
  try {
    const response = await api.get('/config/privacy');
    return response.data;
  } catch (error) {
    console.error('Error fetching privacy config:', error);
    throw error;
  }
};

/**
 * Get feature flags for this instance
 * @returns {Promise} Object with flag keys and boolean values
 */
export const getFeatureFlags = async () => {
  try {
    const response = await api.get('/config/features');
    return response.data;
  } catch (error) {
    console.error('Error fetching feature flags:', error);
    return {};
  }
};

export default api;